<template>
  <template v-for="block in content" :key="block.id">
    <template v-if="block.type !== 'component_block' && block.type !== 'report_block'">
      <component
        :is="getBlock(block)"
        :block="block"
        class="card card-body border-0 stream-content"
      />
    </template>
    <component :is="getBlock(block)" v-else :block="block" class="stream-content" />
  </template>
</template>
<script lang="ts" setup>
import {SphereBlock} from '@/stores/apolloPlatform/cores/portalNavigation';
import * as blocks from '@/components/blocks';
/**
 * DocumentPage, DynamicPage, FolderPage & ReportPage each have a content property.
 * @see {@link apollo_platform/apps/cms/models.py} > SphereContentStreamBlock()
 * In the front-end, each view will render a specific core component alongside any content blocks.
 * `DynamicView.vue` is slightly different in that it handles rendering content blocks only.
 */
const {content} = defineProps<{content: SphereBlock[]}>();
const blockMap = new Map<string, Component>([
  ['contextual_block', blocks.ContextualBlock],
  ['embed_block', blocks.EmbedBlock],
  ['feature_highlight_block', blocks.FeatureHighlightBlock],
  ['carousel_block', blocks.CarouselBlock],
  ['link_cards_block', blocks.LinkCardsBlock],
  ['heading_block', blocks.HeadingBlock],
  ['image_block', blocks.ImageBlock],
  ['paragraph_block', blocks.ParagraphBlock],
  ['block_quote', blocks.QuoteBlock],
  ['table_block', blocks.TableBlock],
  ['report_block', blocks.ReportBlock],
]);

// Our custom blocks for app style components.
import * as portfolio from '@/components/productCatalogue/pages';
const ItsmApp = defineAsyncComponent(() => import('@/components/itsm/ItsmApp.vue'));
const VeeamApp = defineAsyncComponent(() => import('@/components/veeam/VeeamApp.vue'));
const CustomerDiscoveryView = defineAsyncComponent(
  () => import('@/components/customerDiscovery/CustomerDiscoveryApp.vue'),
);
// TODO: Component blocks should strictly match the options from Wagtail.
const appMap = new Map<string, Component>([
  ['product_overview', portfolio.ProductsView],
  ['features', portfolio.FeaturesView],
  ['product_types', portfolio.ProductTypesView],
  ['service_level_objectives', portfolio.SlosView],
  ['product_components', portfolio.ProductComponentsView],
  ['labor_components', portfolio.LaborComponentsView],
  ['component_types', portfolio.ComponentTypesView],
  ['cost_pools', portfolio.CostPoolsView],
  ['service_tickets', ItsmApp],
  ['Veeam', VeeamApp],
  ['customer_discovery', CustomerDiscoveryView],
]); // Apps should handle errors independently.
const getBlock = (block: SphereBlock) =>
  block.type === 'component_block'
    ? appMap.get((block as SphereBlock<'component_block'>).value.name)
    : blockMap.get(block.type);
</script>
<style lang="scss" scoped>
/* Management wants blocks to form one card, skip component blocks, then resume being one card. */

.card {
  border-radius: 0;
  margin-bottom: 0;

  /* Super dumb, but if you don't change the card shadow, they will overlap */
  box-shadow: 0 0.25rem 0.3rem rgb(0 0 0 / 7.5%);
}

.stream-content {
  :deep(h1) {
    margin-block: 4rem 1rem;
  }

  :deep(h2) {
    margin-block: 3rem 1rem;
  }

  :deep(h3) {
    margin-block: 2rem 1rem;
  }

  :deep(h4),
  :deep(h5),
  :deep(h6) {
    margin-block: 1.5rem 1rem;
  }
}

.card-body {
  padding-top: 0;
  padding-bottom: 0;
}

$r: var(--bs-border-radius);

/* First, find elements that do not match ".card" & select the "next-sibling combinator" w/ .card */
:not(.card) + .card {
  margin-top: 1rem;
  padding-top: 1rem;
  border-top-left-radius: $r;
  border-top-right-radius: $r;
}

/* Then, select ".card" classes that are not "next-sibling combinator" w/ .card */
.card:not(:has(+ .card)) {
  border-bottom-left-radius: $r;
  border-bottom-right-radius: $r;
  margin-bottom: 1rem;
}

/* Then, reset the last element, as technically, the previous selection still applies  */
.card:last-of-type {
  margin-bottom: 0;
}

/* Finally, remove the margin for the first element to follow the page title */
.card:nth-child(2) {
  margin-top: 0;
}

/* Nothing before the first card (dynamic page with an "On this page" menu): round it like the others. */
.card:first-child {
  padding-top: 1rem;
  border-top-left-radius: $r;
  border-top-right-radius: $r;
}

/* Feature highlights are standalone cards: space them from each other and from surrounding text. */
.card.feature-highlight,
.card.carousel-block,
.card.link-cards-block {
  margin-block: 1rem;
  padding-block: 1rem;
  border-radius: $r;
}

.card:has(+ .feature-highlight),
.card:has(+ .carousel-block),
.card:has(+ .link-cards-block) {
  border-bottom-left-radius: $r;
  border-bottom-right-radius: $r;
}

.feature-highlight + .card,
.carousel-block + .card,
.link-cards-block + .card {
  margin-top: 1rem;
  padding-top: 1rem;
  border-top-left-radius: $r;
  border-top-right-radius: $r;
}
</style>
